import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { isPretendAiMessage } from '../utils/ChatMessage';
import { type ChatMessage } from '../types/ChatMessage.types';
import { getAiMessageStreaming } from '../redux/chat';

const TOP_BUFFER_PIXELS = 100;

type MessageRef = { message: ChatMessage; ref: RefObject<HTMLDivElement> };

type UseScrollToBottomOfChatArgs = {
    messagesWithRefs: MessageRef[];
    scrollElement?: Element | null;
};

// FIXME: We want to improve the scroll bar a bit on the BotPage and AI Tutor. See https://trello.com/c/pfAxCyso

// oxlint-disable-next-line max-lines-per-function
export function useScrollToBottomOfChat({
    messagesWithRefs,
    scrollElement: providedScrollElement,
}: UseScrollToBottomOfChatArgs): {
    scrollWrapperRef: RefObject<HTMLDivElement | null> | null; // see comment near scrollElement assignment about why this can be null
    chatMessagesContainerRef: RefObject<HTMLDivElement | null>;
} {
    const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

    // When the chat container takes up the vertical space on the page, we return a ref that can be
    // attached to it and it can be used as the scroll target. In the lesson player, the chat container
    // grows following the way the player handles long frame content, so we pass in the scroll container
    // from the player.
    const scrollElement = providedScrollElement || scrollWrapperRef.current;

    const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

    const previousChatHistoryLength = usePrevious(messagesWithRefs.length) ?? 0;

    const aiMessageStreaming = useSelector(getAiMessageStreaming);

    const allMessagesArePretendAiMessages = useMemo(
        () => messagesWithRefs.every(message => isPretendAiMessage(message.message)),
        [messagesWithRefs],
    );

    const streamingMessageIsAtTop = useMemo(() => {
        if (!aiMessageStreaming) return false;

        const { ref: streamingMessageRef } = messagesWithRefs.at(-1)!;
        if (!streamingMessageRef.current) return false;

        const { top } = streamingMessageRef.current.getBoundingClientRect();
        const { top: chatWindowTop } = scrollElement!.getBoundingClientRect();
        return top <= chatWindowTop + TOP_BUFFER_PIXELS;
    }, [aiMessageStreaming, messagesWithRefs, scrollElement]);

    const isAtBottom = useMemo(() => scrollElement?.scrollTop === scrollElement?.scrollHeight, [scrollElement]);

    //---------------------------
    // Scroll to bottom on new message
    //---------------------------

    const scrollToBottomOfChat = useCallback(
        ({ smooth = true }: { smooth?: boolean } = {}) => {
            // Only use scrollTo if browser supports it and when not in the middle of a
            // streaming bot response. The smooth scroll can cause jitters while streaming.
            if (scrollElement?.scrollTo && smooth) {
                scrollElement.scrollTo({
                    top: scrollElement.scrollHeight,
                    behavior: 'smooth',
                });
            } else if (scrollElement) {
                scrollElement.scrollTop = scrollElement.scrollHeight;
            }
        },
        [scrollElement],
    );

    useEffect(() => {
        if (streamingMessageIsAtTop) return;
        if (aiMessageStreaming) {
            // Scrolling smoothly while streaming can cause jitters.
            scrollToBottomOfChat({ smooth: false });
            return;
        }
        if (messagesWithRefs.length > previousChatHistoryLength) {
            scrollToBottomOfChat();
        }
    }, [
        streamingMessageIsAtTop,
        scrollToBottomOfChat,
        aiMessageStreaming,
        previousChatHistoryLength,
        isAtBottom,
        messagesWithRefs,
    ]);

    useEffect(() => {
        let resizeObserver: ResizeObserver;
        let windowResizeListener: () => void;

        if (chatMessagesContainerRef.current) {
            resizeObserver = new ResizeObserver(() => {
                // Scroll to bottom as new greeting messages are added
                if (!allMessagesArePretendAiMessages) return;
                scrollToBottomOfChat();
            });

            resizeObserver.observe(chatMessagesContainerRef.current);

            // this one is necessary for when keyboard pops up in Cordova
            windowResizeListener = () => {
                scrollToBottomOfChat({ smooth: !aiMessageStreaming });
            };

            window.addEventListener('resize', windowResizeListener);
        }

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            if (windowResizeListener) window.removeEventListener('resize', windowResizeListener);
        };
    }, [
        chatMessagesContainerRef,
        scrollToBottomOfChat,
        aiMessageStreaming,
        streamingMessageIsAtTop,
        allMessagesArePretendAiMessages,
    ]);

    return {
        scrollWrapperRef: providedScrollElement ? null : scrollWrapperRef,
        chatMessagesContainerRef,
    };
}
